import { useState } from 'react';
import { productService } from '../../services/api';
import { getAdminToken } from '../utils/authUtils';
import { getApiUrl } from '../../config/environment';
import {
  buildProductPricingFields,
  buildProductPricingPatchBody,
} from '../utils/productPricing';
import { isMongoObjectId } from '../utils/productMongoId';
import { getAdminUser } from '../utils/authUtils';
import {
  canSetProductFeatured,
  omitProductFeaturedForNonAdmin,
} from '../constants/teamAccessPermissions';

const PRICING_ONLY_KEYS = new Set([
  'price',
  'originalPrice',
  'salePrice',
  'isOnSale',
  'discount',
]);

const useUpdateProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateProductPricing = async (productId, { originalPrice, salePrice, isOnSale } = {}) => {
    const id = String(productId || '').trim();
    if (!isMongoObjectId(id)) {
      throw new Error('Invalid product ID for pricing update');
    }
    const body = buildProductPricingPatchBody(originalPrice, salePrice, {
      isOnSaleFlag: isOnSale,
    });
    try {
      setLoading(true);
      setError(null);
      const response = await productService.updateProduct(id, body);
      if (response.success === false) {
        throw new Error(response.message || response.error || 'Failed to update pricing');
      }
      return (
        response.product ||
        response.data?.product ||
        response.data ||
        response
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId, updateData) => {
    try {
      setLoading(true);
      setError(null);

      const id = String(productId || '').trim();
      if (!isMongoObjectId(id)) {
        throw new Error('Invalid product ID for update');
      }

      const adminUser = getAdminUser();
      updateData = omitProductFeaturedForNonAdmin(adminUser, updateData);

      console.log('🔄 Updating product:', id, updateData);

      const definedKeys = Object.keys(updateData || {}).filter(
        (k) => updateData[k] !== undefined
      );
      const isPricingOnlyPatch =
        definedKeys.length > 0 &&
        definedKeys.every((k) => PRICING_ONLY_KEYS.has(k));

      if (isPricingOnlyPatch) {
        const body = buildProductPricingPatchBody(
          updateData.originalPrice,
          updateData.salePrice,
          { isOnSaleFlag: updateData.isOnSale }
        );
        const response = await productService.updateProduct(id, body);
        if (response.success === false) {
          throw new Error(response.message || response.error || 'Failed to update pricing');
        }
        return (
          response.product ||
          response.data?.product ||
          response.data ||
          response
        );
      }
      
      // Prepare the update data according to API documentation
      const finalUpdateData = {};
      
      // Basic fields — only on full product save (never send empty category on pricing-only)
      finalUpdateData.name = updateData.name !== undefined ? updateData.name : '';
      finalUpdateData.description = updateData.description !== undefined ? updateData.description : '';
      if (updateData.descriptionRich !== undefined) {
        finalUpdateData.descriptionRich = updateData.descriptionRich;
      }
      if (updateData.shortDescription !== undefined) finalUpdateData.shortDescription = updateData.shortDescription;
      finalUpdateData.category = updateData.category !== undefined ? updateData.category : '';
      if (updateData.subcategory !== undefined) finalUpdateData.subcategory = updateData.subcategory;
      if (updateData.childrenCategory !== undefined) {
        finalUpdateData.childrenCategory = updateData.childrenCategory;
      }
      if (updateData.mainCategory !== undefined) {
        finalUpdateData.mainCategory = updateData.mainCategory;
      }
      finalUpdateData.brand = updateData.brand !== undefined ? updateData.brand : '';
      
      // Price fields — `price` is selling amount; when on sale it must be salePrice, not originalPrice
      const hasPricingInput =
        updateData.originalPrice !== undefined ||
        updateData.salePrice !== undefined ||
        updateData.price !== undefined ||
        updateData.isOnSale !== undefined;

      if (hasPricingInput) {
        const pricing = buildProductPricingFields(
          updateData.originalPrice ?? updateData.price,
          updateData.salePrice,
          { isOnSaleFlag: updateData.isOnSale }
        );
        finalUpdateData.price = pricing.price;
        finalUpdateData.originalPrice = pricing.originalPrice;
        finalUpdateData.isOnSale = pricing.isOnSale;
        if (pricing.salePrice != null) {
          finalUpdateData.salePrice = pricing.salePrice;
        } else {
          finalUpdateData.salePrice = null;
        }
        if (pricing.discount !== undefined) {
          finalUpdateData.discount = pricing.discount;
        }
      } else if (updateData.discount !== undefined) {
        finalUpdateData.discount = parseFloat(updateData.discount) || undefined;
      }
      
      // Product identifiers
      if (updateData.sku !== undefined) finalUpdateData.sku = updateData.sku;
      if (updateData.url !== undefined) finalUpdateData.url = updateData.url;
      if (updateData.productId !== undefined) finalUpdateData.productId = updateData.productId;
      
      // Arrays
      if (updateData.colors !== undefined) finalUpdateData.colors = updateData.colors;
      if (updateData.sizes !== undefined) finalUpdateData.sizes = updateData.sizes;
      if (updateData.stock !== undefined) finalUpdateData.stock = updateData.stock;
      if (updateData.tags !== undefined) finalUpdateData.tags = updateData.tags;
      if (updateData.features !== undefined) finalUpdateData.features = updateData.features;
      if (updateData.images !== undefined) finalUpdateData.images = updateData.images;
      
      if (updateData.gender !== undefined) {
        finalUpdateData.gender = updateData.gender || 'Unisex';
      }

      // Specifications
      if (updateData.specifications !== undefined) {
        finalUpdateData.specifications = {
          ...updateData.specifications,
          ...(updateData.gender !== undefined
            ? { gender: updateData.gender || 'Unisex' }
            : {}),
        };
      } else if (updateData.gender !== undefined) {
        finalUpdateData.specifications = {
          gender: updateData.gender || 'Unisex',
        };
      }
      
      // Status flags
      if (updateData.isActive !== undefined) finalUpdateData.isActive = updateData.isActive;
      if (updateData.isFeatured !== undefined) finalUpdateData.isFeatured = updateData.isFeatured;
      if (updateData.isNew !== undefined) finalUpdateData.isNew = updateData.isNew;
      if (updateData.isOnSale !== undefined) finalUpdateData.isOnSale = updateData.isOnSale;
      if (updateData.saleEndDate !== undefined) finalUpdateData.saleEndDate = updateData.saleEndDate;
      
      // Physical properties
      if (updateData.weight !== undefined) finalUpdateData.weight = updateData.weight;
      if (updateData.dimensions !== undefined) finalUpdateData.dimensions = updateData.dimensions;
      
      // Nested stock is sent when provided — backend validates active product minimum stock on PUT.
      // Bulk stock API may still run after save for normalization.
      
      console.log('🔄 Final update data being sent:', finalUpdateData);
      
      // Use productService.updateProduct which uses adminApiRequest
      const response = await productService.updateProduct(id, finalUpdateData);
      
      console.log('✅ Product updated successfully:', response);
      
      if (response.success === false) {
        throw new Error(response.message || response.error || 'Failed to update product');
      }

      return (
        response.product ||
        response.data?.product ||
        response.data ||
        response
      );
    } catch (error) {
      console.error('❌ Update product error:', error);
      
      // Handle error messages
      let errorMessage = error.message || 'Failed to update product';
      
      // Parse error if it's a JSON string
      try {
        const errorObj = JSON.parse(errorMessage);
        if (errorObj.error) {
          errorMessage = errorObj.error;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      } catch (e) {
        // Not a JSON string, use as is
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addImagesToProduct = async (productId, imageFiles) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      
      if (!imageFiles || imageFiles.length === 0) {
        throw new Error('Please select at least one image');
      }

      const formData = new FormData();
      
      imageFiles.forEach(file => {
        formData.append('images', file);
      });

      console.log('🖼️ Adding images to product:', productId);
      
      const response = await fetch(getApiUrl(`/products/upload/${productId}/images`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to add images');
      }
      
      console.log('✅ Images added successfully:', result.data);
      return result;
    } catch (error) {
      console.error('❌ Add images error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProductImages = async (productId, imageFiles) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      
      if (!imageFiles || imageFiles.length === 0) {
        throw new Error('Please select at least one image');
      }

      const formData = new FormData();
      
      imageFiles.forEach(file => {
        formData.append('images', file);
      });

      console.log('🖼️ Updating all images for product:', productId);
      
      const response = await fetch(getApiUrl(`/products/upload/${productId}/images/update`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update images');
      }
      
      console.log('✅ Images updated successfully:', result.data);
      return result;
    } catch (error) {
      console.error('❌ Update images error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteProductImages = async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      
      console.log('🗑️ Deleting images only for product:', productId);
      
      const response = await fetch(getApiUrl(`/cloudinary/products/${productId}/images`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete images');
      }
      
      console.log('✅ Images deleted successfully:', result.data);
      return result;
    } catch (error) {
      console.error('❌ Delete images error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update product status flags only (optimized endpoint)
  const updateProductStatus = async (productId, statusData) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      
      const adminUser = getAdminUser();
      if (statusData?.isFeatured !== undefined && !canSetProductFeatured(adminUser)) {
        throw new Error('Only the store admin can change Featured status.');
      }

      console.log('🔄 Updating product status flags:', productId, statusData);
      
      // Prepare status data - only include provided flags
      const statusUpdate = {};
      if (statusData.isActive !== undefined) statusUpdate.isActive = statusData.isActive;
      if (statusData.isFeatured !== undefined) statusUpdate.isFeatured = statusData.isFeatured;
      if (statusData.isNew !== undefined) statusUpdate.isNew = statusData.isNew;
      if (statusData.isOnSale !== undefined) statusUpdate.isOnSale = statusData.isOnSale;
      
      // Use dedicated status endpoint for better performance
      const response = await fetch(getApiUrl(`/admin/products/status/${productId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusUpdate)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update product status');
      }
      
      console.log('✅ Product status updated successfully:', result.data);
      return result;
    } catch (error) {
      console.error('❌ Update product status error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateProduct,
    updateProductPricing,
    updateProductStatus,
    addImagesToProduct,
    updateProductImages,
    deleteProductImages,
    loading,
    error,
  };
};

export default useUpdateProduct;

